/**
 * NewForm design-system Button.
 *
 * New components for the v2 design pass live alongside the legacy shadcn
 * components (`components/ui/button.tsx`) so existing callers keep working
 * unchanged. New code should import from this path:
 *
 *   import { Button } from "@/components/ui/design-system/button";
 *
 * The legacy Button stays at `@/components/ui/button` until everything
 * has migrated. When that day comes, this file becomes the only source of
 * truth and the legacy file gets deleted.
 */

import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  // Base spec mirrors `nf-design-system/preview/components-buttons.html`
  // exactly: `font: 500 14px/1` + 6px radius + 8px icon-text gap. Using
  // arbitrary values `text-[14px]`, `font-[500]`, `leading-none` instead
  // of `text-sm`/`font-medium` because the project overrides Tailwind's
  // `--font-weight-bold`/`-semibold` in `globals.css` — going arbitrary
  // makes the button immune to any further weight-scale tweaks.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-nf-ds-md font-nf-ds-sans text-[14px] font-[500] leading-none ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        brand:
          "bg-nf-ds-purple-product text-nf-ds-white hover:bg-nf-ds-purple-700",
        // Soft-rounded CTAs used across the auth shell (Continue with Google,
        // Sign in →, etc). 2xl radius matches the inputs sitting next to them.
        // Variant names kept as "pill-*" for back-compat with existing callers.
        "pill-primary":
          "h-12 px-nf-ds-6 rounded-nf-ds-2xl bg-nf-ds-black text-nf-ds-white hover:bg-nf-ds-gray-800 shadow-nf-ds-xs",
        "pill-outline":
          "h-12 px-nf-ds-6 rounded-nf-ds-2xl bg-nf-ds-white border border-nf-ds-gray-200 text-nf-ds-black hover:bg-nf-ds-gray-50 shadow-nf-ds-xs",
        // `--destructive` carries the DS error accent — the same
        // hsl(0 84.2% 60.2%) the `err` alert uses for its border and dot.
        // The Tailwind `red-600` this used before was a shade off it and
        // tracked nothing.
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-nf-ds-xs",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4",
        sm: "h-9 px-3 text-[13px] gap-1.5",
        lg: "h-11 px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
